import { Autocomplete, Chip, IconButton, TextField } from '@mui/material';
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import { CloseGlyph } from '../icons';
import RecipientOption from './RecipientOption';
import useStyles from './RecipientsInput.styles';
import type { RecipientsInputProps, ShareRecipient } from './types';
import { useRecipientSearch } from './useRecipientSearch';
import {
  getRecipientLabel,
  isValidEmail,
  normalizeEmail,
  toTypedRecipient,
} from './utils';

const CHIP_DELETE_ICON_SIZE_PX = 12;

/**
 * The address behind an entry, whichever shape it arrived in.
 *
 * A `freeSolo` field hands back the raw string for anything the user
 * typed and a recipient for anything they picked, and MUI passes both
 * shapes to the same callbacks.
 */
const getEntryEmail = (entry: string | ShareRecipient): string =>
  typeof entry === 'string' ? entry : entry.email;

/**
 * Chip field for choosing who to share a response with.
 *
 * Suggestions come from the host's user directory as the user types;
 * anything typed that does not match one is still accepted as a plain
 * address, so the field keeps working when no directory is wired up.
 * A directory pick and a hand-typed address chip up identically —
 * only a malformed address is drawn apart, and the dialog refuses to
 * send while one is present.
 *
 * Typed text becomes a chip on Enter, on leaving the field, and on
 * the space bar once an address is under way.
 *
 * @param recipients - Recipients currently held as chips.
 * @param onRecipientsChange - Receives the full next list on every add or remove.
 */
function RecipientsInput({
  recipients,
  onRecipientsChange,
  className,
}: RecipientsInputProps) {
  const { classes, cx } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const [inputValue, setInputValue] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { options, isLoading } = useRecipientSearch(inputValue);
  const inputRef = useRef<HTMLInputElement>(null);

  // Opening the modal is already the decision to share, so typing
  // starts at once. `autoFocus` fires only on mount, after which the
  // dialog's focus trap can move focus to its container (StrictMode
  // replays its effect); an effect re-runs after it and wins.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const selectedEmails = useMemo(
    () => new Set(recipients.map(({ email }) => normalizeEmail(email))),
    [recipients]
  );

  // Dropping the chosen ones from the list is what prevents a
  // duplicate: there is no second entry left to pick.
  const availableOptions = useMemo(
    () =>
      options.filter(
        (option) => !selectedEmails.has(normalizeEmail(option.email))
      ),
    [options, selectedEmails]
  );

  // A `freeSolo` field has no no-results state of its own: MUI draws
  // the popup's paper whether or not anything landed in it, leaving an
  // empty box under the field. Opening only once there is a row — a
  // match, or the loading line — is what keeps that box away.
  const hasPopupContent = availableOptions.length > 0 || isLoading;

  const handleChange = useCallback(
    (_event: unknown, nextValue: readonly (string | ShareRecipient)[]) => {
      const seenEmails = new Set<string>();
      const nextRecipients: ShareRecipient[] = [];

      for (const entry of nextValue) {
        const recipient =
          typeof entry === 'string' ? toTypedRecipient(entry) : entry;
        const email = normalizeEmail(recipient.email);
        if (email === '' || seenEmails.has(email)) {
          continue;
        }
        seenEmails.add(email);
        nextRecipients.push(recipient);
      }

      onRecipientsChange(nextRecipients);
    },
    [onRecipientsChange]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const pending = inputValue.trim();

      // A name query legitimately holds a space — the host reads
      // anything without an `@` as a name and everything else as an
      // address — so the bar only closes a chip once one is under way.
      // Before that it is an ordinary character, and "Anaya Shah" stays
      // typable.
      if (event.key !== ' ' || !pending.includes('@')) {
        return;
      }

      event.preventDefault();
      handleChange(event, [...recipients, pending]);
      setInputValue('');
    },
    [handleChange, inputValue, recipients]
  );

  return (
    <Autocomplete<ShareRecipient, true, false, true>
      multiple
      freeSolo
      // Without this, text still sitting in the field when the user
      // reaches for Send is dropped on the way out; `autoSelect`
      // commits it as a chip on blur, preferring a highlighted
      // suggestion when the popup is open.
      autoSelect
      disableClearable={false}
      className={cx(classes.root, className)}
      value={recipients}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_event, nextInput) => setInputValue(nextInput)}
      open={isPopupOpen && hasPopupContent}
      onOpen={() => setIsPopupOpen(true)}
      onClose={() => setIsPopupOpen(false)}
      options={availableOptions}
      // The host already matched the query, so re-filtering locally
      // would only hide results it deliberately returned.
      filterOptions={(unfiltered) => unfiltered}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : getRecipientLabel(option)
      }
      // MUI keys each row off its label unless told otherwise, and a
      // label is a display name whenever the directory holds one. Two
      // people of the same name would then share a key, and React
      // would reuse one row's state for the other as results change.
      getOptionKey={(option) =>
        typeof option === 'string' ? option : option.id
      }
      isOptionEqualToValue={(option, value) =>
        normalizeEmail(getEntryEmail(option)) ===
        normalizeEmail(getEntryEmail(value))
      }
      loading={isLoading}
      loadingText={t('chat.share.dialog.loadingText')}
      classes={{ loading: classes.loading }}
      slotProps={{
        paper: { className: classes.paper },
        listbox: { className: classes.listbox },
      }}
      renderOption={({ key, ...optionProps }, option) => (
        <RecipientOption {...optionProps} key={key} recipient={option} />
      )}
      renderTags={(value, getTagProps) =>
        value.map((recipient, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const email = getEntryEmail(recipient);
          const label =
            typeof recipient === 'string'
              ? recipient
              : getRecipientLabel(recipient);
          const isMalformed = !isValidEmail(email);
          const removeAriaLabel: string = t(
            'chat.share.dialog.removeRecipientAriaLabel',
            { recipient: label }
          );
          const invalidTooltip: string = t(
            'chat.share.dialog.invalidRecipientTooltip',
            { recipient: label }
          );
          return (
            <Chip
              {...tagProps}
              key={key}
              className={cx(classes.chip, isMalformed && classes.chipInvalid)}
              // The red on its own says nothing to a screen reader or
              // to anyone who cannot separate the two hues, so the
              // reason rides along as the chip's own title.
              title={isMalformed ? invalidTooltip : undefined}
              label={label}
              // `Chip` clones this element to attach its delete handler,
              // so the node here has to accept `onClick` — a bare glyph
              // would swallow it and leave the chip undeletable.
              deleteIcon={
                <IconButton aria-label={removeAriaLabel}>
                  <CloseGlyph size={CHIP_DELETE_ICON_SIZE_PX} />
                </IconButton>
              }
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          // MUI keys the field from the Autocomplete's own root, which
          // ignores the space bar, so this rides alongside rather than
          // over it.
          onKeyDown={handleKeyDown}
          inputRef={inputRef}
          className={classes.field}
          // The visible "Recipients" label belongs to the dialog body,
          // not to this field, and the placeholder goes as soon as the
          // first chip lands. Naming the input here is what leaves it
          // with a name after that.
          inputProps={{
            ...params.inputProps,
            'aria-label': t('chat.share.dialog.recipientsLabel'),
          }}
          placeholder={
            recipients.length === 0
              ? t('chat.share.dialog.recipientsPlaceholder')
              : undefined
          }
          data-testid="ai-sidekick-share-recipients"
        />
      )}
    />
  );
}

export default RecipientsInput;
